import React from "react";
import ReactDOMServer from "react-dom/server";
import { Bookmark } from "lucide-react";
import { BOOKMARK_TOOL_CONSTANTS } from "../editor.Constant";

/**
 * Renders Lucide Bookmark React component to static HTML string
 * @param {string} iconId - Unique identifier for the icon
 * @returns {string} HTML string representation of the Bookmark icon
 * @author Abhishek Dhillon
 */
const renderBookmarkIconToHtml = (iconId) => {
  return ReactDOMServer.renderToStaticMarkup(
    React.createElement(Bookmark, {
      className: "lucid-icon-common",
      size: 20,
      strokeWidth: 2,
      id: iconId,
      color: "#2a8eb9",
    })
  );
};

/**
 * Extracts SVG element from HTML string by parsing and querying
 * @param {string} htmlString - HTML string containing SVG element
 * @returns {SVGElement|null} Extracted SVG element or null if not found
 * @author Abhishek Dhillon
 */
const extractSvgFromHtml = (htmlString) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;
  return tempDiv.querySelector("svg");
};

/**
 * Creates container attributes object for the bookmark icon wrapper
 * @param {Object} nodeAttrs - Node attributes from ProseMirror node
 * @returns {Object} Container attributes object with class, style, and id
 * @author Abhishek Dhillon
 */
const createContainerAttributes = (nodeAttrs) => {
  return {
    class: `Bookmark-Tool ${nodeAttrs.class || BOOKMARK_TOOL_CONSTANTS.CLASS_NAME}`,
    style: nodeAttrs.style,
    id: nodeAttrs.id,
  };
};

/**
 * Clones and prepares SVG element with necessary classes and attributes
 * @param {SVGElement} svgElement - Original SVG element to clone
 * @returns {SVGElement} Cloned and prepared SVG element
 * @author Abhishek Dhillon
 */
const prepareClonedSvg = (svgElement) => {
  const clonedSvg = svgElement.cloneNode(true);
  clonedSvg.classList.add("lucid-icon-common");
  return clonedSvg;
};

/**
 * Creates fallback SVG DOM structure when React component parsing fails
 * @param {Object} containerAttrs - Container attributes object
 * @param {string} iconId - Unique identifier for the icon
 * @returns {Array} ProseMirror DOM structure array with SVG fallback
 * @author Abhishek Dhillon
 */
const createFallbackSvgDom = (containerAttrs, iconId) => {
  const svgAttrs = {
    xmlns: "http://www.w3.org/2000/svg",
    width: "20",
    height: "20",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    class: "lucid-icon-common",
    id: iconId,
  };
  const pathAttrs = {
    d: "M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z",
  };
  return ["i", containerAttrs, ["svg", svgAttrs, ["path", pathAttrs]]];
};

/**
 * ProseMirror schema definition for bookmark icon node
 * Renders Lucide Bookmark icon as SVG DOM element using lucide-react
 * @author Abhishek Dhillon
 */
export const icon = {
  attrs: {
    class: { default: BOOKMARK_TOOL_CONSTANTS.CLASS_NAME }, // Icon CSS class
    style: { default: null }, // Inline styles
    id: { default: null }, // Unique ID
  },
  group: "inline",
  inline: true,
  selectable: false,
  parseDOM: [
    {
      tag: "i.icon-bookmark",
      getAttrs: (dom) => ({
        class: dom.getAttribute("class") || BOOKMARK_TOOL_CONSTANTS.CLASS_NAME,
        style: dom.getAttribute("style"),
        id: dom.getAttribute("id"),
      }),
    },
  ],
  toDOM: (node) => {
    const iconHtml = renderBookmarkIconToHtml(node.attrs.id);
    const svgElement = extractSvgFromHtml(iconHtml);
    const containerAttrs = createContainerAttributes(node.attrs);

    if (svgElement) {
      const clonedSvg = prepareClonedSvg(svgElement);
      return ["i", containerAttrs, clonedSvg];
    }

    return createFallbackSvgDom(containerAttrs, node.attrs.id);
  },
};